import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS, BUTTON_RADIUS } from "../../../theme/mui/styles";

/**
 * MUI replacement for the VendorModal (create/update vendor).
 * Props:
 * - isOpen, onClose, vendor, loading, handleSubmit(event, data), accountList
 */
export default function VendorModal({
  isOpen,
  onClose,
  vendor,
  loading,
  handleSubmit,
  accountList,
}) {
  const [vendorData, setVendorData] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  const text = !vendor?.id ? "Create" : "Update";

  useEffect(() => {
    if (!isOpen) {
      // Reset form state when closing so a fresh modal opens empty
      setVendorData({});
      setImagePreview(null);
      return;
    }

    setImagePreview(null);
    const { logo, ...vendorDataWithoutLogo } = vendor || {};
    if (logo) {
      setImagePreview(logo);
    }
    setVendorData(vendorDataWithoutLogo);
  }, [vendor, isOpen]);

  const formattedAccountList = useMemo(
    () =>
      (accountList || []).map((account) => ({
        value: account.id,
        label: `${account.number} - ${account.name}`,
      })),
    [accountList]
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setVendorData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSelect = useCallback((selected, name) => {
    setVendorData((prev) => ({ ...prev, [name]: selected.value }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVendorData((prev) => ({ ...prev, [e.target.name]: file }));
    setImagePreview(URL.createObjectURL(file));
  }, []);

  const handleDropImage = useCallback(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      setVendorData((prev) => ({ ...prev, logo: file }));
      setImagePreview(URL.createObjectURL(file));
    },
    []
  );

  const isNameMissing = !vendorData?.name?.trim();

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: DIALOG_RADIUS, mt: 6 } }}
      >
        <DialogTitle
          sx={{
            pr: 5,
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 22,
          }}
        >
          {text} Vendor
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ ml: "auto" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <form
            id="vendorForm"
            onSubmit={(e) => handleSubmit(e, vendorData)}
            encType="multipart/form-data"
          >
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                name="name"
                value={vendorData?.name || ""}
                onChange={handleInputChange}
                required
                fullWidth
                size="small"
              />

              <TextField
                label="Website"
                name="website"
                type="url"
                value={vendorData?.website || ""}
                onChange={handleInputChange}
                fullWidth
                size="small"
              />

              <TextField
                label="Description"
                name="description"
                type="text"
                value={vendorData?.description || ""}
                onChange={handleInputChange}
                fullWidth
                size="small"
              />

              <Box>
                <Typography sx={{ mb: 0.5 }}>Account</Typography>
                <Autocomplete
                  size="small"
                  options={formattedAccountList}
                  value={
                    formattedAccountList.find(
                      (opt) => opt.value === vendorData?.account
                    ) || null
                  }
                  onChange={(event, selected) =>
                    handleSelect(selected || { value: "" }, "account")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select account..."
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ mb: 0.5 }}>Vendor Logo</Typography>
                <Box
                  component="label"
                  onDrop={handleDropImage}
                  onDragOver={(e) => e.preventDefault()}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #536DEC",
                    borderRadius: BUTTON_RADIUS,
                    bgcolor: "#f1f7ff",
                    minHeight: 160,
                    cursor: "pointer",
                    p: 2.5,
                    textAlign: "center",
                    color: "#4b5563",
                  }}
                >
                  <input
                    hidden
                    name="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <ImageIcon sx={{ fontSize: 40, color: "#7c8ba1", mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <Typography component="span" color="primary" fontWeight={600}>
                      Click to upload
                    </Typography>{" "}
                    or drag and drop
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Maximum file size of 1 MB
                  </Typography>
                  {imagePreview && (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Vendor logo preview"
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: BUTTON_RADIUS,
                        objectFit: "cover",
                        border: "1px solid #E5E7EB",
                        mt: 2,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  Choose an image file to upload.
                </Typography>
              </Box>
            </Stack>
          </form>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              px: 3,
              borderRadius: `${BUTTON_RADIUS}px !important`,
              bgcolor: "#9b9b9b",
              color: "#fff",
              "&:hover": { bgcolor: "#8a8a8a" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="vendorForm"
            variant="contained"
            disabled={loading || isNameMissing}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              px: 3,
              borderRadius: `${BUTTON_RADIUS}px !important`,
              bgcolor: "#536DEC",
              "&:hover": { bgcolor: "#536DEC" },
            }}
          >
            {text}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
